import { CommonModule, formatDate } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MenuHeaderTopic, XtenMenuHeaderModule } from '@ui';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { MatDialog } from '@angular/material/dialog';

import { DialogService, SessionStore, ToastService } from '@core';
import { MeterFacade } from '@feature/meter';

import { DailyEnergyChartComponent } from './components/daily-energy-chart/daily-energy-chart.component';
import { EnergyDistributionComponent } from './components/energy-distribution/energy-distribution.component';
import { EnergySummaryComponent } from './components/energy-summary/energy-summary.component';
import { EnergySummaryCardComponent } from './components/energy-summary-card/energy-summary-card.component';

import {
  AnalysisTemplate,
  DailyEnergyPeriod,
} from './data-access/group-analysis.interface';
import { GroupAnalysisFacade } from './data-access/group-analysis.facade';
import { CustomerSiteCardComponentComponent } from './components/customer-site-card.component/customer-site-card.component.component';
import { MdbPowerChartComponent } from './components/mdb-power-chart/mdb-power-chart.component';

@Component({
  selector: 'ems-dashboard',
  standalone: true,
  imports: [
    XtenMenuHeaderModule,
    CommonModule,
    MatCardModule,
    EnergyDistributionComponent,
    EnergySummaryComponent,
    DailyEnergyChartComponent,
    EnergySummaryCardComponent,
    CustomerSiteCardComponentComponent,
    MdbPowerChartComponent
  ],
  providers: [MeterFacade],
  templateUrl: 'ems-dashboard.component.html',
  styleUrls: ['ems-dashboard.component.scss'],
})
export class EMSDashboardComponent implements OnInit, OnDestroy {
  topic: MenuHeaderTopic = {
    title: 'view_value.group_analysis',
    subtitle: 'view_value.group_analysis_detail',
    iconUrl: 'assets/shared/icons/white/explor.svg',
  };

  @ViewChild('reportContent')
  private reportContent?: ElementRef<HTMLElement>;

  readonly templates = this.facade.templates;
  readonly selectedTemplate = this.facade.selectedTemplate;

  readonly dailyEnergyPieChart = this.facade.dailyEnergyPieChart;
  readonly monthlyEnergyPieChart = this.facade.monthlyEnergyPieChart;

  readonly dailyCategories = this.facade.dailyCategories;
  readonly dailySeries = this.facade.dailySeries;

  readonly startDate = this.facade.startDate;
  readonly endDate = this.facade.endDate;

  readonly dailyEnergyPeriod = this.facade.dailyEnergyPeriod;

  readonly totaldailyEnergyPieChart = this.facade.totaldailyEnergyPieChart;

  readonly totalmonthlyEnergyPieChart = this.facade.totalmonthlyEnergyPieChart;

  readonly totalYesterdayEnergyPieChart =
    this.facade.totalYesterdayEnergyPieChart;

  readonly totalLastMonthlyEnergyPieChart =
    this.facade.totalLastMonthlyEnergyPieChart;

  readonly unit = this.facade.unit;

  readonly loading = this.facade.loading;
  readonly loadingDashboard = this.facade.loadingDashboard;
  readonly error = this.facade.error;
  readonly mdbPowerCategories = this.facade.mdbPowerCategories;

  readonly mdbPowerSeries = this.facade.mdbPowerSeries;
  readonly customerSite = this.facade.customerSite;
  exportingPdf = false;

  // ---------------------------------------------------------------------------
  // Carbon Emission
  // ---------------------------------------------------------------------------

  readonly dailyCarbonEmission = () =>
    this.calEmission(this.totaldailyEnergyPieChart());

  readonly monthlyCarbonEmission = () =>
    this.calEmission(this.totalmonthlyEnergyPieChart());

  private calEmission(kwh: number): number {
    return (kwh * 0.4999) / 1000;
  }

  // ---------------------------------------------------------------------------
  // Energy Comparison
  // ---------------------------------------------------------------------------

  readonly dailyComparison = () =>
    this.calComparison(
      this.totaldailyEnergyPieChart(),
      this.totalYesterdayEnergyPieChart(),
    );

  readonly monthlyComparison = () =>
    this.calComparison(
      this.totalmonthlyEnergyPieChart(),
      this.totalLastMonthlyEnergyPieChart(),
    );

  readonly dailyComparisonText = () =>
    this.formatComparison(this.dailyComparison());

  readonly monthlyComparisonText = () =>
    this.formatComparison(this.monthlyComparison());

  private calComparison(current: number, previous: number): number {
    if (previous === 0) {
      return 0;
    }

    return ((current - previous) / previous) * 100;
  }

  private formatComparison(percentage: number): string {
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`;
  }

  // ---------------------------------------------------------------------------
  // Constructor
  // ---------------------------------------------------------------------------

  constructor(
    public facade: GroupAnalysisFacade,
    private dialogService: DialogService,
    private toast: ToastService,
    private readonly dialog: MatDialog,
    private readonly meterFacade: MeterFacade,
    private readonly sessionStore: SessionStore,
  ) {}

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.facade.initialize();
  }

  ngOnDestroy(): void {
    this.facade.destroy();
  }

  // ---------------------------------------------------------------------------
  // Daily Energy
  // ---------------------------------------------------------------------------

  onDailyEnergyPeriodChange(period: DailyEnergyPeriod): void {
    this.facade.changeDailyEnergyPeriod(period);
  }

  // ---------------------------------------------------------------------------
  // PDF Export
  // ---------------------------------------------------------------------------

  async exportPdf(): Promise<void> {
    const element = this.reportContent?.nativeElement;

    if (!element || this.exportingPdf || this.loadingDashboard()) {
      return;
    }

    this.exportingPdf = true;

    try {
      await this.dialogService.runAsyncWithLoading(
        async () => {
          element.classList.add('exporting');

          await this.waitForRender();

          const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
            ignoreElements: (htmlElement) =>
              htmlElement.hasAttribute('data-html2canvas-ignore'),
          });

          const imageData = canvas.toDataURL('image/png', 1);

          this.createPdf(imageData, canvas.width, canvas.height);
        },
        {
          disableClose: true,
          delay: 100,
        },
      );
    } catch (error) {
      console.error('Export PDF error:', error);
      this.toast.error('ไม่สามารถสร้างไฟล์ PDF ได้');
    } finally {
      element.classList.remove('exporting');
      this.exportingPdf = false;
    }
  }

  private createPdf(
    imageData: string,
    canvasWidth: number,
    canvasHeight: number,
  ): void {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const pageWidth = 210;
    const pageHeight = 297;

    // ไม่มีขอบกระดาษ
    const margin = 10;

    const contentWidth = pageWidth - margin * 2;
    const contentHeight = pageHeight - margin * 2;

    const imageHeight = (canvasHeight * contentWidth) / canvasWidth;

    let heightLeft = imageHeight;
    let position = margin;

    pdf.addImage(
      imageData,
      'PNG',
      margin,
      position,
      contentWidth,
      imageHeight,
      undefined,
      'FAST',
    );

    heightLeft -= contentHeight;

    while (heightLeft > 0) {
      position = margin - (imageHeight - heightLeft);

      pdf.addPage();

      pdf.addImage(
        imageData,
        'PNG',
        margin,
        position,
        contentWidth,
        imageHeight,
        undefined,
        'FAST',
      );

      heightLeft -= contentHeight;
    }

    const templateName = this.selectedTemplate()?.name ?? 'group-analysis';

    const fileName = this.toFileName(
      `${templateName}-${this.formatFileDate(new Date())}`,
    );

    pdf.save(`${fileName}.pdf`);
  }

  private waitForRender(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 300);
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // File Name
  // ---------------------------------------------------------------------------

  private formatFileDate(date: Date): string {
    return formatDate(date, 'yyyy-MM-dd-HHmmss', 'th-TH');
  }

  private toFileName(value: string): string {
    return value
      .trim()
      .replace(/[\\/:*?"<>|]+/g, '-')
      .replace(/\s+/g, '-')
      .toLowerCase();
  }

  // ---------------------------------------------------------------------------
  // Display Date
  // ---------------------------------------------------------------------------

  formatDisplayDate(date: Date | null): string {
    if (!date) {
      return '-';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');

    let year = date.getFullYear();

    if (this.sessionStore.getLanguage() === 'th') {
      year = date.getFullYear() + 543;
    }

    return `${day}/${month}/${year}`;
  }
}
